// src/utils/pdfGenerator.js
/* eslint-disable */
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import insightsLogo from "assets/img/insights_logo.PNG";
import {
  lightenColor,
  darkenColor,
  pastelColorToRgb,
  lightenByPercentage,
} from "utils/colorUtils";
import { getProgressColor } from "utils/thresholdUtils";
import { formatNumber } from "utils/formatNumberUtils.js";

// ... (Constantes de Estilo) ...
const FONT_FAMILY_SANS = "helvetica";
const PRIMARY_TEXT_COLOR = "#333333";
const SECONDARY_TEXT_COLOR = "#555555";
const HEADER_FOOTER_TEXT_COLOR = "#777777";
const BORDER_COLOR_LIGHT = "#DDDDDD";
const BORDER_COLOR_TABLE = "#CCCCCC";

const PAGE_MARGIN_TOP = 40;
const PAGE_MARGIN_BOTTOM = 40;
const PAGE_MARGIN_LEFT = 40;
const PAGE_MARGIN_RIGHT = 40;

const HEADER_HEIGHT = 50;
const FOOTER_HEIGHT = 30;

const KPI_TITLE_HEIGHT_ESTIMATE = 30;
const TABLE_TITLE_HEIGHT_ESTIMATE = 25;
const MIN_TABLE_CONTENT_HEIGHT_ESTIMATE = 60;

export const generateKpiReportPdf = ({
  kpiCardsData,
  clientColor, // Este é o clientColor passado pelo componente chamador
  t,
  userDetails,
  reportTitleKey = "kpispage.report.title",
  sectionTitle = null,
  outputFilename = "KPI_Report.pdf",
}) => {
  if (!kpiCardsData || kpiCardsData.length === 0) {
    console.warn("Nenhum dado de KPI fornecido para gerar PDF.");
    return;
  }

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "A4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const usablePageHeight =
    pageHeight - HEADER_HEIGHT - FOOTER_HEIGHT - PAGE_MARGIN_BOTTOM;

  // Usar a cor da implementação DENTRO do userDetails se disponível, senão o clientColor passado, senão fallback
  const effectiveClientColor =
    userDetails?.implementation_color &&
    /^#[0-9A-F]{6}$/i.test(userDetails.implementation_color)
      ? userDetails.implementation_color
      : clientColor && /^#[0-9A-F]{6}$/i.test(clientColor)
        ? clientColor
        : "#4A90E2";

  const headerBgColorRgb = lightenByPercentage(effectiveClientColor, 0.92); // Usa effectiveClientColor
  const tableHeaderBgRgb = darkenColor(effectiveClientColor, 20); // Usa effectiveClientColor
  const kpiTitleColor = effectiveClientColor; // Títulos de KPI usarão a cor efetiva diretamente

  let cursorY = 0;

  const addHeaderAndFooter = (docInstance, pageNum, totalPages) => {
    docInstance.setFillColor(
      headerBgColorRgb.r,
      headerBgColorRgb.g,
      headerBgColorRgb.b
    );
    docInstance.rect(0, 0, pageWidth, HEADER_HEIGHT, "F");

    addHeaderLogo({
      docInstance,
      userDetails,
      headerHeight: HEADER_HEIGHT,
      pageWidth: pageWidth,
      pageMarginRight: PAGE_MARGIN_RIGHT,
    });

    docInstance.setFont(FONT_FAMILY_SANS, "bold");
    docInstance.setFontSize(18);
    docInstance.setTextColor(PRIMARY_TEXT_COLOR);
    docInstance.text(
      t(reportTitleKey),
      PAGE_MARGIN_LEFT,
      HEADER_HEIGHT / 2 + 7
    );

    const generationDate = new Date().toLocaleDateString();
    docInstance.setFont(FONT_FAMILY_SANS, "normal");
    docInstance.setFontSize(8);
    docInstance.setTextColor(HEADER_FOOTER_TEXT_COLOR);
    docInstance.text(
      `${t("kpispage.report.generated_on")}: ${generationDate}`,
      PAGE_MARGIN_LEFT,
      pageHeight - FOOTER_HEIGHT / 2 + 3
    );
    const pageInfoText =
      totalPages > 0
        ? `${t("kpispage.report.page")} ${pageNum} ${t("kpispage.report.from")} ${totalPages}`
        : `${t("kpispage.report.page")} ${pageNum}`;
    docInstance.text(
      pageInfoText,
      pageWidth - PAGE_MARGIN_RIGHT,
      pageHeight - FOOTER_HEIGHT / 2 + 3,
      { align: "right" }
    );
  };

  const startNewPage = () => {
    doc.addPage();
    addHeaderAndFooter(doc, doc.internal.getNumberOfPages(), 0);
    cursorY = HEADER_HEIGHT + PAGE_MARGIN_TOP / 2;
  };

  addHeaderAndFooter(doc, 1, 0);
  cursorY = HEADER_HEIGHT + PAGE_MARGIN_TOP / 2;

  if (sectionTitle) {
    // ... (lógica do título da seção)
    doc.setFont(FONT_FAMILY_SANS, "bold");
    doc.setFontSize(14);
    doc.setTextColor(PRIMARY_TEXT_COLOR);
    if (cursorY + KPI_TITLE_HEIGHT_ESTIMATE > usablePageHeight) {
      startNewPage();
    }
    doc.text(sectionTitle, PAGE_MARGIN_LEFT, cursorY);
    cursorY += 30;
  }

  kpiCardsData.forEach((kpiCard, kpiIndex) => {
    if (
      cursorY +
        KPI_TITLE_HEIGHT_ESTIMATE +
        TABLE_TITLE_HEIGHT_ESTIMATE +
        MIN_TABLE_CONTENT_HEIGHT_ESTIMATE / 2 >
      usablePageHeight
    ) {
      startNewPage();
    }

    doc.setFont(FONT_FAMILY_SANS, "bold");
    doc.setFontSize(12);
    doc.setTextColor(kpiTitleColor); // Usa a cor efetiva do cliente para títulos de KPI
    doc.text(kpiCard.title, PAGE_MARGIN_LEFT, cursorY);
    cursorY += 25;

    // ... (resto do loop de timeAggregations e autoTable)
    const timeAggregations = [
      {
        labelKey: "kpispage.view.monthly",
        data: kpiCard.data?.monthly,
        colHeaderKey: "kpispage.labels.month",
        unit: kpiCard.unit,
      },
      {
        labelKey: "kpispage.view.yearly",
        data: kpiCard.data?.yearly,
        colHeaderKey: "kpispage.labels.year",
        unit: kpiCard.unit,
      },
    ];

    timeAggregations.forEach((agg) => {
      const aggData = agg.data;
      const kpiUnit = agg.unit || "";

      if (aggData && aggData.labels && aggData.labels.length > 0) {
        const tableTitleHeight = TABLE_TITLE_HEIGHT_ESTIMATE;
        const minContentForTable = MIN_TABLE_CONTENT_HEIGHT_ESTIMATE;

        if (
          cursorY + tableTitleHeight + minContentForTable >
          usablePageHeight
        ) {
          startNewPage();
          if (agg.labelKey === "kpispage.view.monthly") {
            doc.setFont(FONT_FAMILY_SANS, "bold");
            doc.setFontSize(12);
            doc.setTextColor(kpiTitleColor); // Usa a cor efetiva
            doc.text(kpiCard.title, PAGE_MARGIN_LEFT, cursorY);
            cursorY += 25;
          }
        }
        doc.setFont(FONT_FAMILY_SANS, "bold");
        doc.setFontSize(10);
        doc.setTextColor(SECONDARY_TEXT_COLOR);
        doc.text(t(agg.labelKey), PAGE_MARGIN_LEFT + 5, cursorY);
        cursorY += 18;

        const head = [
          [
            t(agg.colHeaderKey),
            t("kpispage.labels.target"),
            t("kpispage.labels.actual"),
            t("kpispage.labels.comparison"),
          ],
        ];
        const body = [];

        aggData.labels.forEach((label) => {
          const seriesData = aggData.series;
          const metaVal = seriesData[0]?.[aggData.labels.indexOf(label)];
          const realVal = seriesData[1]?.[aggData.labels.indexOf(label)];

          let comparisonCell = { content: "--", styles: { halign: "center" } };
          if (
            metaVal !== null &&
            metaVal !== undefined &&
            metaVal !== 0 &&
            realVal !== null &&
            realVal !== undefined
          ) {
            const ratio = realVal / metaVal;
            if (isFinite(ratio)) {
              const ratioPct = (ratio * 100).toFixed(1) + "%";
              const baseColor = getProgressColor(
                ratio,
                kpiCard.trackingMode,
                kpiCard.thresholdsConfig
              );
              const pastelRgb = pastelColorToRgb(baseColor);
              comparisonCell = {
                content: ratioPct,
                styles: { fillColor: pastelRgb, halign: "center" },
              };
            }
          } else if (
            metaVal === 0 &&
            (realVal === 0 || realVal === null || realVal === undefined)
          ) {
            comparisonCell = { content: "--", styles: { halign: "center" } };
          } else if (
            metaVal === 0 &&
            realVal !== 0 &&
            realVal !== null &&
            realVal !== undefined
          ) {
            comparisonCell = {
              content: "∞",
              styles: { halign: "center", fontStyle: "italic" },
            };
          }

          body.push([
            { content: label, styles: { halign: "left" } },
            {
              content:
                metaVal !== null && metaVal !== undefined
                  ? formatNumber(metaVal, kpiUnit)
                  : "--",
              styles: { halign: "right" },
            },
            {
              content:
                realVal !== null && realVal !== undefined
                  ? formatNumber(realVal, kpiUnit)
                  : "--",
              styles: { halign: "right" },
            },
            comparisonCell,
          ]);
        });

        const totalPagesBeforeCurrentTable = doc.internal.getNumberOfPages();
        const cursorYBeforeThisTableDraw = cursorY;

        autoTable(doc, {
          startY: cursorY,
          head,
          body,
          theme: "grid",
          styles: {
            font: FONT_FAMILY_SANS,
            fontSize: 8,
            cellPadding: 4,
            textColor: PRIMARY_TEXT_COLOR,
            lineColor: BORDER_COLOR_TABLE,
            lineWidth: 0.5,
          },
          headStyles: {
            fillColor: [
              tableHeaderBgRgb.r, // Usa a cor da tabela derivada da effectiveClientColor
              tableHeaderBgRgb.g,
              tableHeaderBgRgb.b,
            ],
            textColor: "#FFFFFF",
            fontStyle: "bold",
            halign: "center",
            fontSize: 9,
          },
          columnStyles: {
            0: { cellWidth: "auto", halign: "left", fontStyle: "bold" },
            1: { halign: "right", cellWidth: 70 },
            2: { halign: "right", cellWidth: 70 },
            3: { halign: "center", cellWidth: 70 },
          },
          didParseCell: function (data) {
            if (data.section === "body") {
              data.cell.styles.valign = "middle";
            }
          },
          didDrawPage: (dataHook) => {
            if (dataHook.pageNumber > totalPagesBeforeCurrentTable) {
              addHeaderAndFooter(doc, dataHook.pageNumber, 0);
              if (dataHook.settings.startY !== cursorYBeforeThisTableDraw) {
                const currentTitleY = HEADER_HEIGHT + PAGE_MARGIN_TOP / 2;
                doc.setFont(FONT_FAMILY_SANS, "bold");
                doc.setFontSize(12);
                doc.setTextColor(kpiTitleColor); // Usa a cor efetiva
                doc.text(kpiCard.title, PAGE_MARGIN_LEFT, currentTitleY);

                doc.setFontSize(10);
                doc.setTextColor(SECONDARY_TEXT_COLOR);
                doc.text(
                  t(agg.labelKey) + ` (${t("kpispage.report.continued")})`,
                  PAGE_MARGIN_LEFT + 5,
                  currentTitleY + 20
                );
              }
            }
          },
          pageBreak: "auto",
          margin: {
            top: HEADER_HEIGHT + 10,
            right: PAGE_MARGIN_RIGHT,
            bottom: FOOTER_HEIGHT + PAGE_MARGIN_BOTTOM + 10,
            left: PAGE_MARGIN_LEFT,
          },
        });
        cursorY = doc.lastAutoTable.finalY + 25;
      }
    });
    if (kpiIndex < kpiCardsData.length - 1) {
      if (cursorY + 20 > usablePageHeight) {
        startNewPage();
      }
      doc.setDrawColor(BORDER_COLOR_LIGHT);
      doc.line(
        PAGE_MARGIN_LEFT,
        cursorY,
        pageWidth - PAGE_MARGIN_RIGHT,
        cursorY
      );
      cursorY += 20;
    } else {
      cursorY += 10;
    }
  });

  const totalPagesFinal = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPagesFinal; i++) {
    doc.setPage(i);
    doc.setFillColor(255, 255, 255);
    doc.rect(
      0,
      pageHeight - (FOOTER_HEIGHT + PAGE_MARGIN_BOTTOM),
      pageWidth,
      FOOTER_HEIGHT + PAGE_MARGIN_BOTTOM,
      "F"
    );
    addHeaderAndFooter(doc, i, totalPagesFinal);
  }

  doc.save(outputFilename);
};

/**
 * Adds the client logo to a PDF header, vertically centered.
 * @param {jsPDF} docInstance - The jsPDF document instance.
 * @param {object} userDetails - The userDetails object containing the logo.
 * @param {number} headerHeight - The total height of the header area.
 * @param {number} pageWidth - The total width of the page.
 * @param {number} pageMarginRight - The right margin of the page.
 */
export const addHeaderLogo = ({
  docInstance,
  userDetails,
  headerHeight,
  pageWidth,
  pageMarginRight,
}) => {
  try {
    const logoData = userDetails?.implementation_logo || insightsLogo;
    const isBase64 = !!userDetails?.implementation_logo;

    const logoMaxHeight = headerHeight - 15; // Max height with some padding
    const logoMaxWidth = 120; // Max width to prevent oversized logos

    const marginTop = (headerHeight - logoMaxHeight) / 2; // Top margin for the logo area

    let actualFormat = "PNG";
    let dataSource = logoData;

    // --- Logic to handle Base64 and regular image objects ---
    if (isBase64) {
      if (logoData.startsWith("data:image/jpeg;base64,")) {
        actualFormat = "JPEG";
        dataSource = logoData.substring("data:image/jpeg;base64,".length);
      } else if (logoData.startsWith("data:image/png;base64,")) {
        actualFormat = "PNG";
        dataSource = logoData.substring("data:image/png;base64,".length);
      }
      // ... (add other base64 types if needed) ...
      dataSource = dataSource.trim();
    }

    // Get image properties to calculate aspect ratio
    const imgProps = docInstance.getImageProperties(dataSource);
    const aspect = imgProps.height / imgProps.width;

    // Calculate dimensions respecting max width and max height
    let calculatedWidth = logoMaxWidth;
    let calculatedHeight = calculatedWidth * aspect;

    if (calculatedHeight > logoMaxHeight) {
      calculatedHeight = logoMaxHeight;
      calculatedWidth = calculatedHeight / aspect;
    }

    // Calculate the exact Y position to be vertically centered
    const yPos = marginTop + (logoMaxHeight - calculatedHeight) / 2;
    const xPos = pageWidth - calculatedWidth - pageMarginRight;

    docInstance.addImage(
      dataSource,
      actualFormat,
      xPos,
      yPos,
      calculatedWidth,
      calculatedHeight,
      null,
      "NONE"
    );
  } catch (e) {
    console.error("Error adding logo to PDF:", e);
    // Fallback to insightsLogo if the primary logo fails
    if (logoData !== insightsLogo && insightsLogo) {
      try {
        const fallbackProps = docInstance.getImageProperties(insightsLogo);
        const fbAspect = fallbackProps.height / fallbackProps.width;
        let fbWidth = 100;
        let fbHeight = fbWidth * fbAspect;
        const yPos = marginTop + (logoMaxHeight - fbHeight) / 2;
        const xPos = pageWidth - fbWidth - pageMarginRight;
        docInstance.addImage(
          insightsLogo,
          "PNG",
          xPos,
          yPos,
          fbWidth,
          fbHeight,
          null,
          "NONE"
        );
      } catch (fbError) {
        console.error("Failed to add fallback logo:", fbError);
      }
    }
  }
};
